import { useEffect, useLayoutEffect } from 'react'
import { Outlet, Link, useLocation } from 'react-router-dom'
import { LayoutDashboard, Image, Palette, FileText, Folder, Users, Globe, CircleAlert, Settings, ExternalLink, FlaskConical, Lock, Bug, Menu } from 'lucide-react'
import { useConfig } from '@/hooks/useConfig'

export function AdminLayout() {
  const location = useLocation()
  const { data: config } = useConfig()

  // 어드민은 Tailwind 기본 글자 크기를 유지한다 (globals.css 의 :root[data-area="admin"]).
  // 첫 페인트 전에 적용되도록 layout effect 를 쓴다.
  useLayoutEffect(() => {
    document.documentElement.dataset.area = 'admin'
    return () => {
      delete document.documentElement.dataset.area
    }
  }, [])

  useEffect(() => {
    document.title = '픽스젠 어드민'

    return () => {
      document.title = '픽스젠 - AI 이미지 생성기'
    }
  }, [])

  const navItems = [
    { path: '/admin', label: '대시보드', icon: LayoutDashboard },
    { path: '/admin/generations', label: '생성된 이미지', icon: Image },
    { path: '/admin/failed-images', label: '실패한 이미지', icon: CircleAlert },
    { path: '/admin/shared-images', label: '공개된 이미지', icon: Globe },
    { path: '/admin/styles', label: '스타일', icon: Palette },
    { path: '/admin/templates', label: '템플릿', icon: FileText },
    { path: '/admin/categories', label: '템플릿 카테고리', icon: Folder },
    { path: '/admin/users', label: '사용자', icon: Users },
    { path: '/admin/errors', label: '오류 리포트', icon: Bug },
    { path: '/admin/settings', label: '설정', icon: Settings },
  ]

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="flex items-center justify-between px-6 py-4">
          <Link
            to="/admin"
            className="text-xl font-bold tracking-tight text-heading hover:text-primary transition-colors"
          >
            픽스젠 어드민
          </Link>
          <Link
            to="/"
            className="w-9 h-9 flex items-center justify-center rounded-full bg-slate-100 text-slate-600 hover:bg-slate-200 hover:text-slate-800 transition-all duration-200"
            aria-label="서비스로 가기"
            title="서비스로 가기"
          >
            <ExternalLink className="w-[18px] h-[18px]" />
          </Link>
        </div>
      </header>

      {config?.isStaging && (
        <div className="border-b border-amber-300 bg-amber-50 px-3 py-2 md:px-6">
          <div className="flex items-center justify-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-amber-700">
            <FlaskConical className="h-3.5 w-3.5" />
            STAGING — 검증용 환경입니다
          </div>
        </div>
      )}

      {config?.contentReadOnly && (
        <div className="border-b border-amber-300 bg-amber-50 px-4 py-2.5 md:px-6">
          <div className="flex items-center justify-center gap-2 text-center text-xs font-medium text-amber-800 md:text-sm">
            <Lock className="h-4 w-4 flex-shrink-0" />
            <span>
              이 배포는 콘텐츠 읽기 전용입니다. 스타일·템플릿·카테고리·설정(장애 공지 제외)은 웍스(운영) 어드민에서만 수정할 수 있으며, 여기서 변경해도 적용되지 않습니다.
            </span>
          </div>
        </div>
      )}

      <details className="border-b border-border bg-white md:hidden">
        <summary className="flex cursor-pointer items-center gap-2 px-4 py-3 text-sm font-medium text-heading"><Menu className="h-4 w-4" />관리자 메뉴</summary>
        <nav aria-label="관리자 메뉴" className="grid grid-cols-2 gap-1 px-3 pb-3">
          {navItems.map(item => <Link key={item.path} to={item.path} onClick={event => event.currentTarget.closest('details')?.removeAttribute('open')} className="rounded-md px-3 py-3 text-sm text-caption hover:bg-slate-50 focus-visible:outline-2 focus-visible:outline-primary">{item.label}</Link>)}
        </nav>
      </details>
      <div className="flex">
        {/* Sidebar */}
        <aside className="hidden md:block w-64 flex-shrink-0 bg-white border-r border-gray-200 min-h-[calc(100vh-73px)]">
          <nav className="p-4 space-y-1">
            {navItems.map((item) => {
              const isActive = location.pathname === item.path
              const Icon = item.icon
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center space-x-3 px-4 py-2.5 rounded-lg transition-all ${
                    isActive
                      ? 'bg-primary text-white font-semibold shadow-sm'
                      : 'text-caption hover:bg-gray-50 hover:text-body font-medium'
                  }`}
                >
                  <Icon className="w-5 h-5 flex-shrink-0" />
                  <span className="text-[15px] leading-snug">{item.label}</span>
                </Link>
              )
            })}
          </nav>
        </aside>

        {/* Main Content */}
        <main className="min-w-0 flex-1 overflow-x-auto p-4 md:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  )
}
